<template lang="pug">
div
  UiCardSection
    UiStack
      UiStackItem(fill)
        .ui-card-section-title {{ $t("companies.tabs.features") }}
  UiCardSection
    DataTable(
      :embed="true"
      :progress="inProgress"
      :headers="headers"
      :items="tableItems"
      ref="companyFeaturesTable"
      :disablePagination="true"
    )
      template(#cell:is_enabled="{ item }")
        UiCheckbox(
          v-model="item.is_enabled"
          switch
          @update:modelValue="changeEnabled(item)"
        )
      template(#cell:settings="{ item }")
        template(v-if="item.value === 'dimensions_dependence'")
          div
            CompanyDimensionNamesSelect(
              :label="$t(`companies.features.dimension_number`, { number: 1 })"
              v-model="item.settings.dimension_1"
              @update:modelValue="changeDimension"
              :enabled="item.is_enabled"
            )
            CompanyDimensionNamesSelect(
              :label="$t(`companies.features.dimension_number`, { number: 2 })"
              v-model="item.settings.dimension_2"
              @update:modelValue="changeDimension"
              :enabled="item.is_enabled"
            )
            UiFormGroup(v-if="dimensionFeatureChanged && item.is_enabled")
              UiButton(
                variant="primary"
                :disabled="dimensionFeatureInProgress"
                @click="saveDimensions"
              ) {{ $t("actions.save") }}
        template(v-else-if="Object.entries(item.settings).length !== 0")
          UiStack(
            vertical
            spacing="sm"
          )
            template(v-for="(value, name) in item.settings")
              UiCheckbox(
                v-if="['cost_centers_set', 'prioritize_keywords_methods', 'prioritize_vendors_methods'].includes(name)"
                v-model="item.settings[name]"
                switch
                size="sm"
                @update:modelValue="changeEnabled(item)"
              ) {{ $t(`companies.features.${name}`) }}
        template(v-else)
          p None
      template(#cell:actions="{ item }")
        UiStack(spacing="xs")
          UiStackItem
            UiButton(
              size="sm"
              variant="smooth-danger"
              @click="deleteReport(item)"
              icon="Trash"
            ) {{ $t("actions.delete") }}

  UiCardSection(v-if="automationFeatureChanged")
    UiStack(vertical)
      UiAlert(
        variant="warning"
        :message="$t(`companies.features.automation_feature_reminder`)"
        :closable="false"
      )
      UiStack
        UiStackItem(fill)
          UiFormGroup(:vuelidateModel="v$.invoiceGuesserFeature.comment")
            UiInput(
              v-model="invoiceGuesserFeature.comment"
              placeholder="Changes comment"
              :error="v$.invoiceGuesserFeature.comment.$error"
            )
        UiStackItem
          UiButton(
            variant="primary"
            @click="changeSettings(null)"
            icon="Check"
          ) {{ $t("actions.save") }}

  UiCardSection
    .limited-with.mt-4
      UiStack(vertical)
        h5 {{ $t("companies.features.settings") }}
        div
          UiFormGroup
            UiCheckbox#automate-accounting-input(
              v-model="company.automate_accounting"
              switch
            ) {{ $t(`companies.features.automate_accounting`) }}
          UiFormGroup
            UiCheckbox#deduct-vat-input(
              v-model="company.deduct_vat"
              switch
            ) {{ $t(`companies.features.deduct_vat`) }}
          UiFormGroup
            UiCheckbox#overwrite-suggestions-input(
              v-model="company.overwrite_suggestions"
              switch
            ) {{ $t(`companies.features.overwrite_suggestions`) }}
          UiFormGroup(v-if="invoiceGuesserFeature.settings")
            UiCheckbox#voucher-date-input(
              v-model="invoiceGuesserFeature.settings.voucher_date"
              switch
            ) {{ $t(`companies.features.voucher_date`) }}
  UiCardSection
    UiStack(vertical)
      UiStack
        UiStackItem(fill)
          UiFormGroup(
            :label="$t('companies.features.comment_user')"
            label-for="comment-user-input"
            label-cols="4"
          )
            UiInput#comment-user-input(
              v-model="invoiceGuesserFeature.comment_user"
            )
        UiStackItem(fill)
          // empty block
      UiStack
        UiStackItem(fill)
          UiFormGroup(
            :label="$t('companies.basic.iban')"
            label-for="iban-input"
          )
            UiInput#iban-input(v-model="company.iban")
        UiStackItem(fill)
          UiFormGroup(
            :label="$t('companies.basic.cur_iban')"
            label-for="cur-iban-input"
          )
            UiInput#cur-iban-input(v-model="company.cur_iban")
      UiStack
        UiStackItem(fill)
          UiFormGroup(
            :label="$t('companies.basic.bic')"
            label-for="bic-input"
          )
            UiInput#bic-input(v-model="company.bic")
        UiStackItem(fill)
          UiFormGroup(
            :label="$t('companies.basic.cur_bic')"
            label-for="cur-bic-input"
          )
            UiInput#cur-bic-input(v-model="company.cur_bic")

  UiCardSection(v-if="showScope")
    .mt-4
      h5 {{ $t("companies.features.scope_sales_invoices_text") }}
      UiStack(vertical)
        UiRadio#option1(
          v-model="scopeSalesInvoicesOption"
          @update:modelValue="changeScopeSalesInvoicesText"
          value="option1"
        ) {{ $t("companies.features.scope_sales_invoices_text_option_1") }}
        UiRadio#option2(
          v-model="scopeSalesInvoicesOption"
          @update:modelValue="changeScopeSalesInvoicesText"
          value="option2"
        ) {{ $t("companies.features.scope_sales_invoices_text_option_2") }}

  UiCardSection(v-if="showScope")
    .mt-4
      h5 {{ $t("companies.features.scope_dimension_items") }}
      UiStack
        UiStackItem(fill)
          UiFormGroup(
            :label="$t('companies.features.dimension_name')"
            :vuelidateModel="v$.company.scope_dimension_items.dimension_name"
          )
            UiInput#dimension-name-input(
              v-model="company.scope_dimension_items.dimension_name"
              :error="v$.company.scope_dimension_items.dimension_name.$error"
            )
        UiStackItem(fill)
          UiFormGroup(
            :label="$t('companies.features.dimension_item_name')"
            :vuelidateModel="v$.company.scope_dimension_items.dimension_item_name"
          )
            UiInput#dimension-item-name-input(
              v-model="company.scope_dimension_items.dimension_item_name"
              :error="v$.company.scope_dimension_items.dimension_item_name.$error"
            )

  UiCardSection(v-if="showScope")
    .mt-4
      h5 {{ $t("companies.features.scope_maventa_invoice_prefix") }}
      UiStack(spacing="sm" align="center")
        UiStackItem
          UiInputLabel {{ $t("companies.features.prefix") }}
        UiStackItem
          UiInput(
            v-model="company.scope_settings.maventa_invoice_prefix"
            style="width: 120px"
          )

  UiCardSection(v-if="showScope")
    h5 {{ $t("companies.features.scope_invoice_number_starting_symbols") }}
    UiStack(
      vertical
      spacing="sm"
    )
      UiStackItem
        UiDataTable(
          :headers="invoicesStartingSymbolsHeaders"
          :items="invoicesStartingSymbols"
        )
          template(#tbody)
            ScopeInvoiceNumberStartingSymbolTableRow(
              v-for="item in invoicesStartingSymbols"
              :headers="invoicesStartingSymbolsHeaders"
              :items="invoicesStartingSymbols"
              :item="item"
              @delete="deleteSymbol(item)"
            )
        DataTableEmptyState(v-if="!invoicesStartingSymbols.length")
          template(#description)
            div
              p {{ $t("companies.features.no_symbols") }}
              UiButton.add-symbol-button(
                variant="smooth-primary"
                size="sm"
                @click="addSymbol()"
                icon="Plus"
              ) {{ $t("companies.features.add_symbol") }}
      UiStackItem(v-if="invoicesStartingSymbols.length")
        UiButton(
          variant="smooth-primary"
          size="sm"
          @click="addSymbol()"
          icon="Plus"
        ) {{ $t("companies.features.add_symbol") }}

  UiCardSection(v-if="showScope")
    h5 {{ $t("companies.features.scope_sales_invoices_print_codes") }}
    UiStack(
      vertical
      spacing="sm"
    )
      UiStackItem
        UiDataTable(
          :headers="scopePrintCodesHeaders"
          :items="scopePrintCodes"
        )
          template(#tbody)
            SaleInvoicePrintCodeTableRow(
              v-for="item in scopePrintCodes"
              :headers="scopePrintCodesHeaders"
              :items="scopePrintCodes"
              :languages="scopePrintCodesLanguages"
              :item="item"
              @delete="deleteScopePrintCode(item)"
            )
        DataTableEmptyState(v-if="!scopePrintCodes.length")
          template(#description)
            div
              p {{ $t("companies.features.no_symbols") }}
              UiButton.add-symbol-button(
                variant="smooth-primary"
                size="sm"
                @click="addScopePrintCode()"
                icon="Plus"
              ) {{ $t("companies.features.add_print_code") }}
      UiStackItem(v-if="scopePrintCodes.length")
        UiButton(
          variant="smooth-primary"
          size="sm"
          @click="addScopePrintCode()"
          icon="Plus"
        ) {{ $t("companies.features.add_print_code") }}

  UiCardSection.features-save-bar
    UiButton(
      variant="primary"
      @click="changeSettings(null)"
      icon="Check"
    ) {{ $t("actions.save_changes") }}
</template>

<script>
import ApiCompanyFeatures from "@/api/companyFeatures.js";
import ApiCompanies from "@/api/companies.js";
import CompanyDimensionNamesSelect from "./features/CompanyDimensionNamesSelect.vue";
import SaleInvoicePrintCodeTableRow from "./features/SaleInvoicePrintCodeTableRow.vue";
import ScopeInvoiceNumberStartingSymbolTableRow from "./features/ScopeInvoiceNumberStartingSymbolTableRow.vue";
import ApiCompanyReports from "@/api/companyReports";
import { scrollToFirstInvalidField } from "@/common/scroll_to_first_invalid";
import useVuelidate from "@vuelidate/core";
import { requiredIf } from "@vuelidate/validators";

const scopePrintCodesLanguages = ["en", "fi"];

function getPrintCodeItem(item = {}) {
  const result = { key: "" };
  scopePrintCodesLanguages.forEach((code) => {
    result[code] = "";
  });
  return {
    ...result,
    ...item,
  };
}

function getCompanyScopePrintCodesList(company) {
  const hash = {};

  function readToHash(data, language) {
    Object.entries(data ?? {}).forEach(([key, value]) => {
      if (!hash[key]) {
        hash[key] = getPrintCodeItem({ key });
      }
      hash[key][language] = value;
    });
  }

  scopePrintCodesLanguages.forEach((code) => {
    readToHash(company.scope_print_codes[code], code);
  });

  return Object.values(hash);
}

function serializeScopePrintCodes(list) {
  const result = {};

  // Prefill result languages
  scopePrintCodesLanguages.forEach((language) => {
    result[language] = {};
  });

  list.forEach((item) => {
    scopePrintCodesLanguages.forEach((language) => {
      result[language][item.key] = item[language] ?? "";
    });
  });

  return result;
}

function getScopeSalesInvoicesOption(scope_settings) {
  if (scope_settings["new_comments"] === true) {
    return "option2";
  }
  return "option1";
}

export default {
  components: {
    CompanyDimensionNamesSelect,
    ScopeInvoiceNumberStartingSymbolTableRow,
    SaleInvoicePrintCodeTableRow,
  },

  setup() {
    return {
      v$: useVuelidate(),
    };
  },

  validations() {
    return {
      invoiceGuesserFeature: {
        comment: {
          required: requiredIf(this.automationFeatureChanged),
        },
      },
      company: {
        scope_dimension_items: {
          dimension_name: {
            required: requiredIf(
              this.company.scope_dimension_items.dimension_item_name,
            ),
          },
          dimension_item_name: {
            required: requiredIf(
              this.company.scope_dimension_items.dimension_name,
            ),
          },
        },
      },
    };
  },

  data() {
    return {
      loaded: false,
      inProgress: false,
      pagination: {
        sortBy: null,
      },
      headers: [
        {
          key: "name",
          label: "Name",
          squeeze: false,
        },
        {
          key: "is_enabled",
          label: "Enabled",
          squeeze: true,
        },
        {
          key: "settings",
          label: "Settings",
          squeeze: false,
        },
      ],
      items: [],
      settings: {},
      invoiceGuesserFeature: {
        comment: "",
      },
      company: {
        scope_dimension_items: {
          dimension_name: null,
          dimension_item_name: null,
        },
        scope_settings: {},
      },
      invoicesStartingSymbols: [],
      scopePrintCodes: [],
      matchFile: null,
      automationFeatureChanged: false,
      dimensionFeatureChanged: false,
      dimensionFeatureInProgress: false,
      showScope: false,
      scopeSalesInvoicesOption: "option1",
    };
  },

  computed: {
    scopePrintCodesLanguages() {
      return scopePrintCodesLanguages;
    },

    isDimensionsDependenceAvailable() {
      return this.items.some((feature) => {
        return feature?.settings?.cost_centers_set ?? false;
      });
    },

    tableItems() {
      return this.items.filter((item) => {
        if (item.value === "dimensions_dependence") {
          return this.isDimensionsDependenceAvailable;
        }
        return true;
      });
    },

    invoicesStartingSymbolsHeaders() {
      return [
        {
          key: "symbol",
          label: this.$t("companies.features.symbol"),
          width: "50%",
        },
        {
          key: "count",
          label: this.$t("companies.features.count"),
          width: "50%",
        },
        {
          key: "actions",
          label: "",
          squeeze: true,
        },
      ];
    },

    scopePrintCodesHeaders() {
      return [
        {
          key: "key",
          label: this.$t("companies.features.print_code"),
          width: "160px",
        },
        {
          key: "en",
          label: this.$t("language.en"),
        },
        {
          key: "fi",
          label: this.$t("language.fi"),
        },
        {
          key: "actions",
          label: "",
          squeeze: true,
        },
      ];
    },
  },

  methods: {
    handleInvalidForm() {
      this.$toaster.add({
        type: "error",
        title: this.$t("errors.validation_error"),
        message: this.$t("errors.fill_required_fields"),
      });

      this.$nextTick(() => {
        scrollToFirstInvalidField(this.$el);
      });
    },

    handleServerError(error) {
      this.$toaster.add({
        type: "error",
        title: this.$t("errors.operation_error"),
        message:
          error?.response?.data?.error ?? this.$t("errors.something_went_wrong"),
      });
    },

    handleSaveSuccess() {
      this.$toaster.add({
        type: "success",
        title: this.$t("companies.features.settings_updated"),
      });
    },

    getData() {
      this.inProgress = true;

      const featuresRequest = ApiCompanyFeatures.fetchFeatures(
        this.$route.params.id,
      ).then((resp) => {
        this.items = resp.data.features;
        this.invoiceGuesserFeature =
          this.items.filter((f) => f.value == "invoice_guesser")[0] ?? {};
        this.invoiceGuesserFeature.settings.prioritize_keywords_methods =
          this.invoiceGuesserFeature.settings.prioritize_keywords_methods ||
          false;
        this.invoiceGuesserFeature.settings.prioritize_vendors_methods =
          this.invoiceGuesserFeature.settings.prioritize_vendors_methods ||
          false;
      });

      const companyRequest = ApiCompanies.getCompany(
        this.$route.params.id,
      ).then((resp) => {
        this.company = {
          ...resp.data.company,
          invoices_starting_symbols:
            resp.data.company.invoices_starting_symbols ?? {},
          scope_print_codes: resp.data.company.scope_print_codes ?? {},
          scope_dimension_items: {
            dimension_name: null,
            dimension_item_name: null,
            ...resp.data.company.scope_dimension_items,
          },
          scope_settings: resp.data.company.scope_settings ?? {},
        };
        this.invoicesStartingSymbols = Object.entries(
          this.company.invoices_starting_symbols,
        ).map(([symbol, count]) => ({
          symbol,
          count,
        }));

        this.scopePrintCodes = getCompanyScopePrintCodesList(this.company);
        this.scopeSalesInvoicesOption = getScopeSalesInvoicesOption(
          this.company.scope_settings,
        );
      });

      Promise.all([featuresRequest, companyRequest])
        .then(() => {
          this.inProgress = false;
        })
        .catch((error) => {
          this.inProgress = false;
          this.handleServerError(error);
        });
    },

    changeEnabled(item) {
      if (item.id != this.invoiceGuesserFeature.id) {
        ApiCompanyFeatures.updateFeature(this.$route.params.id, item.id, {
          feature: { is_enabled: item.is_enabled },
        })
          .then((resp) => {
            item = resp.data.feature;
          })
          .catch((error) => {
            this.handleServerError(error);
          });
      } else {
        this.automationFeatureChanged = true;
      }
    },

    changeSettings(item) {
      this.v$.$validate().then((res) => {
        if (res === false) {
          this.handleInvalidForm();
          return;
        }

        let params = {
          feature: this.invoiceGuesserFeature,
        };

        let companyParams = {
          company: {
            automate_accounting: this.company.automate_accounting,
            deduct_vat: this.company.deduct_vat,
            iban: this.company.iban,
            bic: this.company.bic,
            cur_iban: this.company.cur_iban,
            cur_bic: this.company.cur_bic,
            overwrite_suggestions: this.company.overwrite_suggestions,
            invoices_starting_symbols:
              this.getSerializedInvoicesStartingSymbols(),
            scope_print_codes: this.getSerializedScopePrintCodes(),
            scope_dimension_items: this.getSerializedScopeDimensions(),
            scope_settings: this.company.scope_settings,
          },
        };

        if (item != null) {
          params.feature.settings = item.settings;
        }

        params.feature.comment = this.invoiceGuesserFeature.comment;

        ApiCompanyFeatures.updateFeature(
          this.$route.params.id,
          this.invoiceGuesserFeature.id,
          params,
        )
          .then(() =>
            ApiCompanies.updateCompany(this.$route.params.id, companyParams),
          )
          .then(() => {
            this.automationFeatureChanged = false;
            this.v$.$reset();
            this.getData();
            this.handleSaveSuccess();
          })
          .catch((error) => {
            this.handleServerError(error);
          });
      });
    },

    changeDimension(value) {
      console.log("changeDimension", value);

      this.dimensionFeatureChanged = true;
    },

    async saveDimensions() {
      this.dimensionFeatureInProgress = true;
      const valid = await this.v$.$validate();
      if (!valid) {
        this.handleInvalidForm();
        this.dimensionFeatureInProgress = false;
        return;
      }
      const feature = this.items.find(
        (f) => f.value == "dimensions_dependence",
      );
      try {
        await ApiCompanyFeatures.updateFeature(
          this.$route.params.id,
          feature.id,
          { feature },
        );
        this.dimensionFeatureChanged = false;
        this.handleSaveSuccess();
      } catch (error) {
        this.handleServerError(error);
      }
      this.dimensionFeatureInProgress = false;
    },

    showScopeReport() {
      ApiCompanyReports.showScopeReport(this.$route.params.id)
        .then((resp) => {
          this.showScope = resp.data.showScopeReport;
        })
        .catch(() => {
          this.showScope = false;
        });
    },

    addSymbol() {
      this.invoicesStartingSymbols.push({ symbol: "", count: 0 });
    },

    deleteSymbol(item) {
      const index = this.invoicesStartingSymbols.indexOf(item);
      this.invoicesStartingSymbols.splice(index, 1);
    },

    getSerializedInvoicesStartingSymbols() {
      const result = {};
      this.invoicesStartingSymbols.forEach((item) => {
        if (item.symbol) {
          result[item.symbol] = item.count;
        }
      });
      return result;
    },

    getSerializedScopePrintCodes() {
      return serializeScopePrintCodes(this.scopePrintCodes);
    },

    getSerializedScopeDimensions() {
      const { dimension_name, dimension_item_name } =
        this.company.scope_dimension_items;

      if (!dimension_name && !dimension_item_name) {
        return {};
      }

      return { dimension_name, dimension_item_name };
    },

    changeScopeSalesInvoicesText(option) {
      if (option === "option2") {
        this.company.scope_settings["new_comments"] = true;
        return;
      }
      this.company.scope_settings["new_comments"] = false;
    },

    addScopePrintCode() {
      this.scopePrintCodes.push(getPrintCodeItem());
    },

    deleteScopePrintCode(item) {
      const index = this.scopePrintCodes.indexOf(item);
      this.scopePrintCodes.splice(index, 1);
    },
  },

  mounted() {
    this.getData();
    this.showScopeReport();
  },
};
</script>

<style scoped>
.add-symbol-button {
  margin-top: -20px;
}

.limited-with {
  max-width: 640px;
}

.features-save-bar.ui-card-section {
  position: sticky;
  bottom: 0;
  z-index: 2;
  padding-top: 16px;
  background: var(--bb-app-bg-main);
  border-top: 1px solid var(--bb-gray-800);
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  box-shadow: 0 -4px 12px rgba(var(--bb-shadow-rgb), 0.08);
}
</style>
